import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { addSampleQuestions } from './SampleQuestions';
import BulkUpload from './BulkUpload';
import './QuestionManager.css';

const QuestionManager = ({ userRole, onBack }) => {
  const [questions, setQuestions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [filters, setFilters] = useState({
    subject: '',
    chapter: '',
    class: '',
    difficulty: ''
  });
  const [newQuestion, setNewQuestion] = useState({
    question: '',
    options: ['', '', '', ''],
    correctAnswer: '',
    subject: '',
    chapter: '',
    class: '1',
    difficulty: 'easy',
    hint: ''
  });
  const [showSubjectUpdater, setShowSubjectUpdater] = useState(false);
  const [subjectMappings, setSubjectMappings] = useState([{ from: '', to: '' }]);

  useEffect(() => {
    fetchQuestions();
    fetchAcademicStructure();
  }, [filters]);

  const fetchAcademicStructure = async () => {
    try {
      const [subjectsRes, chaptersRes, topicsRes] = await Promise.all([
        axios.get('/api/subjects'),
        axios.get('/api/chapters'),
        axios.get('/api/topics')
      ]);
      setSubjects(subjectsRes.data);
      setChapters(chaptersRes.data);
      setTopics(topicsRes.data);
    } catch (error) {
      console.error('Error fetching academic structure:', error);
    }
  };

  const fetchQuestions = async () => {
    try {
      console.log('Fetching questions with filters:', filters);
      const response = await axios.get('/api/quiz/questions/manage', { params: filters });
      console.log('Fetched questions:', response.data.length);
      setQuestions(response.data);
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/quiz/questions', newQuestion);
      setNewQuestion({
        question: '',
        options: ['', '', '', ''],
        correctAnswer: '',
        subject: '',
        chapter: '',
        class: '1',
        difficulty: 'easy',
        hint: ''
      });
      setShowAddForm(false);
      fetchQuestions();
    } catch (error) {
      console.error('Error adding question:', error);
    }
  };

  const handleEditQuestion = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/api/quiz/questions/${editingQuestion._id}`, editingQuestion);
      setEditingQuestion(null);
      fetchQuestions();
    } catch (error) {
      console.error('Error updating question:', error);
    }
  };

  const handleDeleteQuestion = async (id) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      try {
        await axios.delete(`/api/quiz/questions/${id}`);
        fetchQuestions();
      } catch (error) {
        console.error('Error deleting question:', error);
      }
    }
  };

  const updateNewQuestionOption = (index, value) => {
    const updatedOptions = [...newQuestion.options];
    updatedOptions[index] = value;
    setNewQuestion({ ...newQuestion, options: updatedOptions });
  };

  const updateEditingQuestionOption = (index, value) => {
    const updatedOptions = [...editingQuestion.options];
    updatedOptions[index] = value;
    setEditingQuestion({ ...editingQuestion, options: updatedOptions });
  };

  if (!['teacher', 'admin', 'admin1', 'teacher_admin_level1'].includes(userRole)) {
    return (
      <div className="access-denied">
        <h2>Access Denied</h2>
        <p>Only teachers and admins can manage questions.</p>
        <button onClick={onBack}>Back to Games</button>
      </div>
    );
  }

  return (
    <div className="question-manager">
      <div className="manager-header">
        <h2>Question Manager</h2>
        <div className="header-actions">
          <button onClick={() => setShowAddForm(true)} className="add-btn">
            Add New Question
          </button>
          <button onClick={addSampleQuestions} className="sample-btn">
            Add Sample Questions
          </button>
          {['admin1', 'teacher_admin_level1'].includes(userRole) && (
            <>
              <button onClick={() => setShowBulkUpload(true)} className="bulk-btn">
                Bulk Upload JSON
              </button>
              <button 
                onClick={() => setShowSubjectUpdater(true)} 
                className="update-btn" 
                style={{background: '#ff6b35', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer'}}
              >
                🔄 Update Subjects
              </button>
            </>
          )}
          <button onClick={onBack} className="back-btn">
            Back to Games
          </button>
        </div>
      </div>

      <div className="filters">
        <select 
          value={filters.subject} 
          onChange={(e) => setFilters({...filters, subject: e.target.value})}
        >
          <option value="">All Subjects</option>
          {subjects.map(subject => (
            <option key={subject._id} value={subject.name}>{subject.name}</option>
          ))}
        </select>
        
        <select 
          value={filters.class} 
          onChange={(e) => setFilters({...filters, class: e.target.value})}
        >
          <option value="">All Classes</option>
          {['admin1', 'teacher_admin_level1'].includes(userRole) ? (
            // Admin Level 1 can see all classes 1-12
            Array.from({length: 12}, (_, i) => (
              <option key={i+1} value={i+1}>Class {i+1}</option>
            ))
          ) : (
            // Regular teachers see limited classes
            [1,2,3,4,5].map(cls => (
              <option key={cls} value={cls}>Class {cls}</option>
            ))
          )}
        </select>
        
        <select 
          value={filters.difficulty} 
          onChange={(e) => setFilters({...filters, difficulty: e.target.value})}
        >
          <option value="">All Difficulties</option>
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
      </div>

      {showAddForm && (
        <div className="modal-overlay">
          <div className="question-form">
            <h3>Add New Question</h3>
            <form onSubmit={handleAddQuestion}>
              <textarea
                placeholder="Enter question"
                value={newQuestion.question}
                onChange={(e) => setNewQuestion({...newQuestion, question: e.target.value})}
                required
              />
              
              {newQuestion.options.map((option, index) => (
                <input
                  key={index}
                  type="text"
                  placeholder={`Option ${String.fromCharCode(65 + index)}`}
                  value={option}
                  onChange={(e) => updateNewQuestionOption(index, e.target.value)}
                  required
                />
              ))}
              
              <select
                value={newQuestion.correctAnswer}
                onChange={(e) => setNewQuestion({...newQuestion, correctAnswer: e.target.value})}
                required
              >
                <option value="">Select Correct Answer</option>
                {newQuestion.options.map((option, index) => (
                  <option key={index} value={option}>{option}</option>
                ))}
              </select>
              
              <select
                value={newQuestion.subject}
                onChange={(e) => setNewQuestion({...newQuestion, subject: e.target.value})}
                required
              >
                <option value="">Select Subject</option>
                {subjects.map(subject => (
                  <option key={subject._id} value={subject.name}>{subject.name}</option>
                ))}
              </select>
              
              <input
                type="text"
                placeholder="Chapter"
                value={newQuestion.chapter}
                onChange={(e) => setNewQuestion({...newQuestion, chapter: e.target.value})}
                required
              />
              
              <select
                value={newQuestion.class}
                onChange={(e) => setNewQuestion({...newQuestion, class: e.target.value})}
              >
                {['admin1', 'teacher_admin_level1'].includes(userRole) ? (
                  // Admin Level 1 can create questions for all classes 1-12
                  Array.from({length: 12}, (_, i) => (
                    <option key={i+1} value={i+1}>Class {i+1}</option>
                  ))
                ) : (
                  // Regular teachers limited to classes 1-5
                  [1,2,3,4,5].map(cls => (
                    <option key={cls} value={cls}>Class {cls}</option>
                  ))
                )}
              </select>
              
              <select
                value={newQuestion.difficulty}
                onChange={(e) => setNewQuestion({...newQuestion, difficulty: e.target.value})}
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
              
              <input
                type="text"
                placeholder="Hint (optional)"
                value={newQuestion.hint}
                onChange={(e) => setNewQuestion({...newQuestion, hint: e.target.value})}
              />
              
              <div className="form-actions">
                <button type="submit">Add Question</button>
                <button type="button" onClick={() => setShowAddForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="questions-list">
        {questions.map((question) => (
          <div key={question._id} className="question-card">
            <div className="question-header">
              <span className="question-meta">
                {question.subject} - Class {question.class} - {question.chapter}
              </span>
              <span className={`difficulty ${question.difficulty}`}>
                {question.difficulty}
              </span>
            </div>
            
            <div className="question-text">{question.question}</div>
            
            <div className="options-list">
              {question.options.map((option, index) => (
                <div 
                  key={index} 
                  className={`option ${option === question.correctAnswer ? 'correct' : ''}`}
                >
                  {String.fromCharCode(65 + index)}: {option}
                </div>
              ))}
            </div>
            
            {question.hint && (
              <div className="hint">Hint: {question.hint}</div>
            )}
            
            <div className="question-actions">
              <button onClick={() => setEditingQuestion(question)}>Edit</button>
              <button onClick={() => handleDeleteQuestion(question._id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editingQuestion && (
        <div className="modal-overlay">
          <div className="question-form">
            <h3>Edit Question</h3>
            <form onSubmit={handleEditQuestion}>
              <textarea
                value={editingQuestion.question}
                onChange={(e) => setEditingQuestion({...editingQuestion, question: e.target.value})}
                required
              />
              
              {editingQuestion.options.map((option, index) => (
                <input
                  key={index}
                  type="text"
                  value={option}
                  onChange={(e) => updateEditingQuestionOption(index, e.target.value)}
                  required
                />
              ))}
              
              <select
                value={editingQuestion.correctAnswer}
                onChange={(e) => setEditingQuestion({...editingQuestion, correctAnswer: e.target.value})}
                required
              >
                {editingQuestion.options.map((option, index) => (
                  <option key={index} value={option}>{option}</option>
                ))}
              </select>
              
              <div className="form-actions">
                <button type="submit">Update Question</button>
                <button type="button" onClick={() => setEditingQuestion(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBulkUpload && (
        <BulkUpload
          onClose={() => setShowBulkUpload(false)}
          onSuccess={() => {
            fetchQuestions();
            setShowBulkUpload(false);
          }}
        />
      )}

      {showSubjectUpdater && (
        <div className="modal-overlay">
          <div className="question-form" style={{maxWidth: '600px'}}>
            <h3>🔄 Update Quiz Subjects</h3>
            <p>Map old subject names to new ones. All questions will be updated.</p>
            
            {subjectMappings.map((mapping, index) => (
              <div key={index} style={{display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center'}}>
                <input
                  type="text"
                  placeholder="From (e.g., Math)"
                  value={mapping.from}
                  onChange={(e) => {
                    const updated = [...subjectMappings];
                    updated[index].from = e.target.value;
                    setSubjectMappings(updated);
                  }}
                  style={{flex: 1}}
                />
                <span>→</span>
                <input
                  type="text"
                  placeholder="To (e.g., Mathematics)"
                  value={mapping.to}
                  onChange={(e) => {
                    const updated = [...subjectMappings];
                    updated[index].to = e.target.value;
                    setSubjectMappings(updated);
                  }}
                  style={{flex: 1}}
                />
                <button 
                  type="button" 
                  onClick={() => setSubjectMappings(subjectMappings.filter((_, i) => i !== index))}
                  style={{background: '#dc3545', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px'}}
                >
                  ✕
                </button>
              </div>
            ))}
            
            <button 
              type="button" 
              onClick={() => setSubjectMappings([...subjectMappings, { from: '', to: '' }])}
              style={{background: '#28a745', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', marginBottom: '15px'}}
            >
              + Add Mapping
            </button>
            
            <div className="form-actions">
              <button 
                type="button" 
                onClick={async () => {
                  try {
                    const validMappings = subjectMappings.filter(m => m.from && m.to);
                    if (validMappings.length === 0) {
                      alert('Please add at least one valid mapping');
                      return;
                    }
                    
                    let totalUpdated = 0;
                    for (const mapping of validMappings) {
                      const response = await axios.put('/api/quiz/questions/update-subject', {
                        oldSubject: mapping.from,
                        newSubject: mapping.to
                      });
                      totalUpdated += response.data.modifiedCount;
                    }
                    
                    alert(`✅ Updated ${totalUpdated} questions successfully!`);
                    setShowSubjectUpdater(false);
                    setSubjectMappings([{ from: '', to: '' }]);
                    fetchQuestions();
                  } catch (error) {
                    alert('❌ Error updating subjects: ' + error.message);
                  }
                }}
                style={{background: '#007bff'}}
              >
                Update Subjects
              </button>
              <button type="button" onClick={() => {
                setShowSubjectUpdater(false);
                setSubjectMappings([{ from: '', to: '' }]);
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionManager;